import PropTypes from "prop-types";
import { useEffect, useMemo, useRef, useState } from "react";

// Helpers
import { clone } from "@/Common/helpers/Modifiers/formatters";
import { isLastValid, isValidMatrixRange } from "@/Common/helpers/Matrices/tiers";

// Defaults sections
import { GrowthBonusTier, MarketShareTier } from "@/PayPlans/helpers/DefaultSections";

// Hooks
import { useCallbackState } from "@/Common/hooks/CallbackState";
import { useValidations } from "@/Common/hooks/Validations";

// Layouts
import TierAlertError from "@/Layouts/components/Tiers/TierAlertError";
import TierColumns from "@/Layouts/components/Tiers/TierColumns";
import TierColumnsItem from "@/Layouts/components/Tiers/TierColumnsItem";
import TierRows from "@/Layouts/components/Tiers/TierRows";

// Custom components
import FrequencySelector from "@/PayPlans/components/Commons/FrequencySelector";
import MatrixColumn from "./MatrixColumn";
import MatrixRow from "./MatrixRow";

// MUI
import { Collapse, Grid, Stack } from "@mui/material";
import { TransitionGroup } from "react-transition-group";

// Constants
const minimumColumns = 1;
const maximumColumns = 4;
const maximumTiers = Number.MAX_VALUE;

const minimumGrossInput = 0;
const maximumGrossInput = 100;

const minimumGrowthInput = -100;
const maximumGrowthInput = 10000;

const isTierConditionsValid = ({ isSomeConfigured, tiers = [] }) => {
	const isLastBonusValid = isLastValid(tiers, { column: "bonuses", reference: "bonus" });
	const isMatrixInRange = isValidMatrixRange(tiers, { column: "bonuses" });
	return isSomeConfigured || (isLastBonusValid && isMatrixInRange);
};

const isCommissionValid = ({ isSomeConfigured, tiers = [] }) =>
	isSomeConfigured || isLastValid(tiers, { column: "bonuses", reference: "bonus" });

export default function MarketShare(props) {
	const {
		make = "",
		frequency = "MoM",
		tiers = [],
		isSomeConfigured = true,
		readOnly = false,
		onActionUpdate = () => {},
		onActionValidation = () => {},
	} = props;

	// Hooks
	const { errors, isValid, getErrorsMessages, validate } = useValidations();
	const localErrorsMessages = getErrorsMessages();

	const headerErrors = errors?.get("regionalRanks");
	const rowsErrors = errors?.get("nationalRanks");
	const columnsErrors = errors?.get("bonuses");
	const minimumRangeError = Boolean(errors?.get("range")?.length);

	// Control to send update actions
	const wasUpdated = useRef(false);

	const onModelUpdate = (updatedModel) => {
		const isAllValid = isValid && isTierConditionsValid({ isSomeConfigured, tiers: updatedModel?.tiers || [] });
		onActionValidation?.(make, isAllValid);
		if (wasUpdated.current) {
			wasUpdated.current = false;
			onActionUpdate?.(make, updatedModel);
		}
	};

	// States
	const [disableAddColumn, setDisableAddColumn] = useState(false);
	const [innerModel, setInnerModel] = useCallbackState(
		{
			tiers: [],
			frequency: "MoM",
		},
		onModelUpdate
	);

	// Control Last Bonus Error
	const lastBonusError = !isCommissionValid({ isSomeConfigured, tiers: innerModel?.tiers || [] });

	// Action handlers
	const handleUpdateFrequency = (selection) => {
		update({ ...innerModel, frequency: selection.value });
	};

	const handleAddColumn = () => {
		const updatedTiers = [...innerModel.tiers];
		const bonusInput = updatedTiers?.[0]?.["bonuses"]?.[0]?.["bonus"]?.["input"] || "Percent";
		const lastIndex = updatedTiers?.[0]?.["bonuses"]?.length ? updatedTiers[0]["bonuses"].length - 1 : 0;

		updatedTiers.forEach((tier) => {
			tier.bonuses.push(GrowthBonusTier(0, maximumGrowthInput, { bonus: 0, input: bonusInput }));
			if (tier?.bonuses?.[lastIndex]) {
				tier.bonuses[lastIndex]["to"]["value"] = 0;
			}
		});

		wasUpdated.current = true;
		setInnerModel({ ...innerModel, tiers: updatedTiers });
	};

	const handleDeleteColumn = () => {
		const updatedTiers = [...innerModel.tiers];
		if (updatedTiers?.[0]?.["bonuses"]?.length > minimumColumns) {
			updatedTiers.forEach((tier) => {
				tier.bonuses.splice(-1);
				const lastIndex = tier?.bonuses?.length ? tier.bonuses.length - 1 : 0;
				tier.bonuses[lastIndex]["to"]["value"] = maximumGrowthInput;
			});
		}
		update({ ...innerModel, tiers: updatedTiers });
	};

	const handleAddTier = () => {
		const updatedTiers = [...innerModel.tiers];
		const lastIndex = updatedTiers?.length ? updatedTiers.length - 1 : 1;
		const lastInput = updatedTiers?.[lastIndex]?.["bonuses"]?.[0]?.["bonus"]?.["input"] || "Percent";
		const previousBonusesLength = updatedTiers?.[lastIndex]?.["bonuses"]?.length;

		const bonuses = [];
		for (let i = 0; i < previousBonusesLength; i++) {
			const previousFrom = updatedTiers?.[0]?.["bonuses"]?.[i]?.["from"]?.["value"] || 0;
			const previousTo = updatedTiers?.[0]?.["bonuses"]?.[i]?.["to"]?.["value"] || 0;
			bonuses[i] = GrowthBonusTier(previousFrom, previousTo, { bonus: 0, input: lastInput });
		}

		updatedTiers.push(MarketShareTier(minimumGrossInput + 0.01, maximumGrossInput, bonuses));

		if (updatedTiers?.length) {
			updatedTiers[lastIndex].to.value = minimumGrossInput;
		}

		wasUpdated.current = true;
		setInnerModel({ ...innerModel, tiers: updatedTiers });
	};

	const handleDeleteTier = () => {
		const updatedTiers = [...innerModel.tiers];
		if (updatedTiers.length >= 1) {
			updatedTiers.splice(-1);
		}
		const lastIndex = updatedTiers?.length ? updatedTiers.length - 1 : 0;
		updatedTiers[lastIndex].to.value = maximumGrossInput;
		update({ ...innerModel, tiers: updatedTiers });
	};

	const handleUpdateValue = (row, column, location, area, value) => {
		const updatedTiers = [...innerModel.tiers];
		const updatedValue = value ? Number(value) : 0;

		switch (location) {
			case "column":
				updatedTiers.forEach((tier) => {
					tier.bonuses?.forEach((bonus, key) => {
						if (area in bonus && key === column) {
							bonus[area]["value"] = updatedValue;
							if (tier.bonuses?.[key + 1]) {
								tier.bonuses[key + 1]["from"]["value"] = (updatedValue + 0.01).toFixed(2);
							}
						}
					});
				});
				break;
			case "row":
				if (area !== "bonus") {
					updatedTiers[row][area]["value"] = updatedValue;
					if (updatedTiers?.[row + 1] && area === "to") {
						updatedTiers[row + 1]["from"]["value"] = (updatedValue + 0.01).toFixed(2);
					}
				} else {
					updatedTiers[row]["bonuses"][column][area]["value"] = updatedValue;
				}
				break;
			default:
				break;
		}

		update({ ...innerModel, tiers: updatedTiers });
	};

	const handleUpdateInput = (row, column, location, area, input) => {
		const updatedTiers = [...innerModel.tiers];
		switch (location) {
			case "column":
				break;
			case "row":
				updatedTiers.forEach((tier) => {
					tier.bonuses?.forEach((bonus) => {
						if (area in bonus) {
							bonus[area]["input"] = input;
							bonus[area]["value"] = 0;
						}
					});
				});
				break;
			default:
				break;
		}
		update({ ...innerModel, tiers: updatedTiers });
	};

	const update = (updatedModel) => {
		wasUpdated.current = true;
		setInnerModel(updatedModel);
	};

	useMemo(() => {
		const innerTiers = innerModel?.tiers || [];
		if (innerTiers?.length) {
			validate([
				{
					definition: {
						useKey: "nationalRanks",
						operators: {
							conditions: [
								{
									type: "INLINE",
									on: "value",
									compare: "<", // If from < to is good,
								},
								{
									type: "STEP",
									on: "value",
									compare: "===",
									deltas: { Percent: 0.01 }, // Add delta on upper value
									distinct: 0,
								},
							],
						},
						errorsMap: ["from", "to"],
						message: "Invalid tiers",
						validationType: "DOUBLE_INPUT",
						specialProperties: {
							noFirstError: true,
							noLastError: true,
						},
					},
					section: innerTiers || [],
				},
				{
					definition: {
						useKey: "regionalRanks",
						operators: {
							conditions: [
								{
									type: "INLINE",
									on: "value",
									compare: "<", // If from < to is good,
								},
								{
									type: "STEP",
									on: "value",
									compare: "===",
									deltas: { Percent: 0.01 }, // Add delta on upper value
								},
							],
						},
						errorsMap: ["from", "to"],
						message: "Invalid Market Share Growth tiers",
						validationType: "DOUBLE_INPUT",
						specialProperties: {
							noFirstError: true,
							noLastError: true,
						},
					},
					section: innerTiers?.[0]?.bonuses || [],
				},
				{
					definition: {
						useKey: "bonuses",
						operators: {
							conditions: [
								{
									order: "ROWS",
									type: "STEP",
									on: "value",
									compare: "<=", // If current <= next is good,
								},
								{
									order: "COLUMNS",
									type: "STEP",
									on: "value",
									compare: "<",
									deltas: { Dollar: 0, Percent: 0 },
								},
							],
						},
						errorsMap: ["bonus"],
						errorsMapOn: "bonuses",
						message: "Incorrect bonuses definition",
						validationType: "MATRIX",
					},
					section: innerTiers,
				},
				{
					definition: {
						useKey: "range",
						validationType: "CUSTOM",
						errorsMap: ["tiers"],
						message: "Please add at least a two tiers configuration",
						operators: {
							customFn: (params = null) => {
								const { checkTiers = [], someConfigured = true } = params || {};
								if (someConfigured) {
									return [{ tiers: false }];
								}
								const isValidRange = isValidMatrixRange(checkTiers, { column: "bonuses" });
								return [{ tiers: !isValidRange }];
							},
							params: {
								checkTiers: innerTiers,
								someConfigured: isSomeConfigured,
							},
						},
					},
					section: [{ tiers: false }],
				},
			]);
		}
	}, [innerModel, isSomeConfigured, validate]);

	useEffect(() => {
		setInnerModel({
			frequency,
			tiers: clone(tiers),
		});
		return () => {
			wasUpdated.current = false;
			setInnerModel({
				frequency: "MoM",
				tiers: [],
			});
		};
	}, [tiers, frequency, setInnerModel]);

	const handleDisableFrequency = (disabledFrequencies) => {
		setDisableAddColumn(disabledFrequencies?.length === 2);
	};

	return (
		<Grid container item mt={2} xs={12}>
			<Grid item xs={12}>
				<TierColumns
					titleLabel={"Market Share Growth %"}
					titleContainerProps={{
						justifyContent: "flex-start",
						alignItems: "end",
						sx: { maxWidth: "241px", width: "241px", marginRight: "33px", marginLeft: "-29px" },
					}}
					titleProps={{
						className: "tiered-title tiered-title-adjustment",
						sx: null,
					}}
					disabledDelete={innerModel?.tiers?.[0]?.bonuses?.length <= minimumColumns || readOnly}
					disabledAdd={disableAddColumn || innerModel?.tiers?.[0]?.bonuses?.length >= maximumColumns || readOnly}
					onClickDelete={() => handleDeleteColumn()}
					onClickAdd={() => handleAddColumn()}
				>
					<Stack direction={"row"} position={"relative"}>
						{innerModel?.tiers?.[0]?.bonuses?.map((tier, key) => {
							const fromDisabled = Boolean(!key);
							const toDisabled = Boolean(innerModel?.tiers?.[0]?.bonuses.length === key + 1);

							return (
								<TierColumnsItem key={key} index={key} title={"Bonus"}>
									<MatrixColumn
										index={key}
										item={tier}
										errors={headerErrors || []}
										fromDisabled={fromDisabled}
										toDisabled={toDisabled}
										readOnly={readOnly}
										onActionUpdate={handleUpdateValue}
									/>
								</TierColumnsItem>
							);
						})}
					</Stack>
				</TierColumns>

				<Stack mt={"-40px"}>
					<FrequencySelector
						make={make}
						section={"MarketShare"}
						sx={{ width: "200px" }}
						disabled={readOnly}
						readOnly={readOnly}
						onChange={(selection) => handleUpdateFrequency(selection)}
						onDisabledFrequency={handleDisableFrequency}
						selected={innerModel?.frequency}
					/>
				</Stack>
			</Grid>

			<Grid item xs={12} mt={2} minHeight={"135px"}>
				<TierRows
					showTitle
					title={"Market Share %"}
					sxTitlePropsContainer={{
						top: 130,
						left: -18,
					}}
					centerTitleAfterRow={2}
					deleteButtonText={"Remove Tier"}
					addButtonText={"Add Tier"}
					deleteButtonDisabled={innerModel?.tiers?.length <= 1 || readOnly}
					addButtonDisabled={innerModel?.tiers?.length >= maximumTiers || readOnly}
					spaces={{
						mainSection: 235,
						columns: 195,
						totalColumns: innerModel?.tiers?.[0]?.bonuses?.length || 1,
						totalRows: innerModel?.tiers?.length || 1,
						separation: 5.5,
					}}
					rowsAreaProps={{
						ml: "35px",
					}}
					onClickDelete={() => handleDeleteTier()}
					onClickAdd={() => handleAddTier()}
				>
					<TransitionGroup>
						<Collapse>
							<Stack direction="column" rowGap={2}>
								{innerModel?.tiers?.map((tier, key) => {
									const rowKey = `market_share_row_${key}`;
									const fromDisabled = Boolean(!key);
									const toDisabled = Boolean(innerModel?.tiers?.length === key + 1);
									const bonusesLength = innerModel?.tiers?.[0]?.bonuses?.length || 0;
									const reduction = bonusesLength * 10 + 45 + bonusesLength;

									const getColumnsErrors = (nativeErrors = [], complainErrors = false) => {
										if (nativeErrors?.length) {
											return nativeErrors;
										}
										return [[{ bonus: complainErrors }]];
									};

									return (
										<Grid key={rowKey} container item>
											<MatrixRow
												index={key}
												item={tier}
												fromDisabled={fromDisabled}
												toDisabled={toDisabled}
												spaces={{
													mainSection: 235,
													columns: 195,
													totalColumns: innerModel?.tiers?.[0]?.bonuses?.length || 1,
													totalRows: innerModel?.tiers?.length || 1,
													separation: 5.5,
													reduction,
												}}
												rowsErrors={rowsErrors || []}
												columnsErrors={getColumnsErrors(columnsErrors, minimumRangeError)}
												lastBonusError={lastBonusError}
												readOnly={readOnly}
												spacing={"10.5px"}
												sxMainContent={{ minWidth: "235px" }}
												sxCells={{ minWidth: "184.42px" }}
												onActionUpdate={handleUpdateValue}
												onChangeType={handleUpdateInput}
											/>
										</Grid>
									);
								})}
							</Stack>
						</Collapse>
					</TransitionGroup>

					{Boolean(localErrorsMessages?.length || lastBonusError) && (
						<Grid container direction="column" rowSpacing={0.2} mt={1} alignItems="flex-start">
							{localErrorsMessages.map((message, key) => (
								<TierAlertError key={`error_market_share_${message}_${key}`} text={message} />
							))}
							{lastBonusError && <TierAlertError text={"Last commission must be configured"} />}
						</Grid>
					)}
				</TierRows>
			</Grid>
		</Grid>
	);
}
MarketShare.propTypes = {
	make: PropTypes.string,
	frequency: PropTypes.string,
	tiers: PropTypes.array,
	isSomeConfigured: PropTypes.bool,
	readOnly: PropTypes.bool,
	onActionUpdate: PropTypes.func,
	onActionValidation: PropTypes.func,
};
